import { ReactElement } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import config from 'const/config'
import {
  FAL_ANGLE_LEFT,
  FAR_ANGLE_DOWN,
  FAR_ENVELOPE,
  FAR_GLOBE,
  FAR_TIMES,
  FAS_USER_CIRCLE
} from 'const/icons'
import routes, { Routes } from 'const/routes'
import i18n from 'strings/i18n'
import useWindowWidth from 'syf-component-library/ui/customHooks/useWindowWidth'
import useLogoutRedirect from 'hooks/useLogoutRedirect'
import { useAtomValue, useSetAtom } from 'jotai'
import {
  featureFlagsFetchedAtom,
  givenNameAtom,
  identityTypeAtom,
  modalStatusAtom
} from 'store/atoms'
import hasOnlySetpayOrPaylaterAccounts from 'helpers/hasOnlySetpayOrPaylaterAccounts'
import { useCreditAccounts } from 'const/syfApiQueries'
import isFeatureFlagEnabled from 'helpers/isFeatureFlagEnabled'
import { isBrandRefreshEnabled } from 'helpers/getBrandRefreshPhase'
import SERVICING_ID_IDENTITY from 'const/identityTypes'
import redirectToUrl from 'helpers/redirectToUrl'
import handleRevokeAccessToken from 'helpers/handleRevokeAccessToken'
import SystemNavigation from 'syf-component-library/ui/patterns/SystemNavigation'
import {
  V3LogoutSystemNavigation,
  V3SystemNavigation
} from 'pages/V3SystemNavigation'
import { getBoolFromApptimize } from 'helpers/getBoolFromApptimize'
import Search from 'ui/atoms/Search'
import {
  AlertsIcon,
  AlertsIconLabel,
  AlertsIconWithLabelWrapper,
  NavigationContainer
} from './subcomponents'
import { buildDropdownList } from './dropdownFactory'

export interface HeaderProps {
  handleLanguageChange: () => void
}

const backButtonPaths: Routes[] = [
  routes.ACCOUNT_SERVICES,
  routes.ACTIVITY,
  routes.ALERTS,
  routes.CREDIT_BALANCE_REFUND,
  routes.DISPUTES,
  routes.MARKETING_PRIVACY,
  routes.PROFILE,
  routes.PROMOTIONS,
  routes.REWARDS,
  routes.CREDIT_SCORE,
  routes.CREDIT_VIEW,
  routes.STATEMENTS,
  routes.DOCUMENT_CENTER
]

const alertsButtonVisibilityPaths: Routes[] = [...backButtonPaths, routes.HOME]

const AlertsIconWithLabel = () => {
  return (
    <AlertsIconWithLabelWrapper className="alerts-icon-with-label">
      <AlertsIcon>
        <FontAwesomeIcon icon={FAR_ENVELOPE} />
      </AlertsIcon>
      <AlertsIconLabel>{i18n({ button: 'alerts' }) as string}</AlertsIconLabel>
    </AlertsIconWithLabelWrapper>
  )
}

const getBrandRefreshUiConfig = (
  brandRefreshEnabled: boolean,
  isLessThanTablet: boolean,
  assetsHost: string
) => {
  const mobileLogo = brandRefreshEnabled ? 'syf-icon-dark' : 'syf_icon'
  const desktopLogo = brandRefreshEnabled ? 'syf-logo-dark' : 'logo'
  const logo = isLessThanTablet ? mobileLogo : desktopLogo
  return {
    logoSrc: `${assetsHost}/assets/synchrony/${logo}.svg`,
    SystemNavigationWrapper: brandRefreshEnabled
      ? V3SystemNavigation
      : SystemNavigation,
    LogoutNavigationWrapper: brandRefreshEnabled
      ? V3LogoutSystemNavigation
      : SystemNavigation
  }
}

const Header = ({ handleLanguageChange }: HeaderProps): ReactElement => {
  const { ASSETS_HOST, SECURITY_CENTER_URL } = config
  const location = useLocation()
  const locationPathname: Routes = location.pathname
  const givenName = useAtomValue(givenNameAtom)
  const identityType = useAtomValue(identityTypeAtom)
  const isFeatureFlagsFetched = useAtomValue(featureFlagsFetchedAtom)
  const {
    data: { accounts = [] } = {},
    isLoading: isCreditAccountsLoading,
    isError: isCreditAccountsAPIError
  } = useCreditAccounts()
  const shouldDisplayDocumentCenter =
    isFeatureFlagEnabled('isDocumentCenterEnabled') &&
    !hasOnlySetpayOrPaylaterAccounts(accounts)

  const lwsRedirectUri = sessionStorage.getItem('lwsRedirectUri')
  const isLwsRedirect = !!lwsRedirectUri

  const shouldDisplayMissingSynchronyAccount =
    isFeatureFlagsFetched &&
    !isCreditAccountsLoading &&
    !isCreditAccountsAPIError &&
    !isLwsRedirect &&
    isFeatureFlagEnabled('isAddAccountABTestingEnabled') &&
    getBoolFromApptimize('shouldShowAddAccountInAvatar')
  const shouldDisplaySignInSecurity =
    isFeatureFlagEnabled('isSignInSecurityEnabled') &&
    identityType === SERVICING_ID_IDENTITY
  const updateModalStatus = useSetAtom(modalStatusAtom)
  const windowWidth = useWindowWidth()
  const { isLogoutDisabled, handleLogoutAndRedirect } = useLogoutRedirect()

  const navigate = useNavigate()
  const isLWSErrorPage = locationPathname === routes.LWS_ERROR
  const isLogoutRoute = locationPathname === routes.LOGOUT

  const handleOnClick = () => {
    const { _SFDDL: SFDDL } = window
    const pageFunction = SFDDL?.pageInfo?.PageFunction
    handleLogoutAndRedirect(pageFunction, 'customermenu')
  }

  const handleOnProfileClick = () => {
    navigate(routes.PROFILE)
  }

  const handleOnDocumentCenterClick = () => {
    navigate(routes.DOCUMENT_CENTER)
  }

  const handleOnMarketingSettingClick = () => {
    navigate(routes.MARKETING_PRIVACY)
  }

  const handleAlertsClick = () => navigate(routes.ALERTS)
  const handleCreditScoreClick = () => navigate(routes.CREDIT_SCORE)
  const handleSignInSecurity = () => {
    ;(async () => {
      const languageId = localStorage.getItem('language')
      await handleRevokeAccessToken()
      redirectToUrl(`${SECURITY_CENTER_URL}/?ui_locales=${languageId}`)
    })()
  }

  const handleOnMissingSynchronyAccountClick = () => {
    updateModalStatus('AddAccount', 'add-account-avatar-menu-id')
  }

  const handleBackButtonClick = () => navigate(routes.HOME)

  const showBackButton = backButtonPaths.includes(locationPathname)

  const isAlertsPath = alertsButtonVisibilityPaths.includes(locationPathname)

  const alertsVisibleOnDesktop = !windowWidth.isLessThanTablet && isAlertsPath
  const alertsVisibleOnMobile = windowWidth.isLessThanTablet && isAlertsPath

  const dropDownList = buildDropdownList({
    shouldDisplayDocumentCenter,
    shouldDisplayMissingSynchronyAccount,
    shouldDisplaySignInSecurity,
    alertsVisibleOnMobile,
    isLogoutDisabled,
    onProfileClick: handleOnProfileClick,
    onDocumentCenterClick: handleOnDocumentCenterClick,
    onMarketingClick: handleOnMarketingSettingClick,
    onAlertsClick: handleAlertsClick,
    onCreditScoreClick: handleCreditScoreClick,
    onMissingSynchronyAccountClick: handleOnMissingSynchronyAccountClick,
    onSecurityClick: handleSignInSecurity,
    onLogoutClick: handleOnClick
  })
  const conditionalNavigationProps = isLWSErrorPage
    ? {}
    : {
        dropDownList,
        ...(alertsVisibleOnDesktop && {
          alertsTooltip: i18n({ button: 'alertsTooltip' }) as string,
          alertsIcon: <AlertsIconWithLabel />,
          handleAlertsClick
        })
      }

  // Apptimize-gated rebrand: phase >= 1 swaps logo assets and the V3 navigation.
  const brandRefreshEnabled = isBrandRefreshEnabled()
  const { logoSrc, SystemNavigationWrapper, LogoutNavigationWrapper } =
    getBrandRefreshUiConfig(
      brandRefreshEnabled,
      windowWidth.isLessThanTablet,
      ASSETS_HOST
    )
  const languageProps = {
    handleLanguageClick: handleLanguageChange,
    language: i18n({ button: 'languageText' }) as string,
    languageIcon: <FontAwesomeIcon icon={FAR_GLOBE} />
  }

  return (
    <NavigationContainer data-cy="vista-header" data-test="vista-header">
      {isLogoutRoute ? (
        <LogoutNavigationWrapper
          logo={logoSrc}
          altText="Synchrony"
          {...(brandRefreshEnabled && languageProps)}
        />
      ) : (
        <>
          <SystemNavigationWrapper
            altText="Synchrony"
            closeIcon={<FontAwesomeIcon icon={FAR_TIMES} size="lg" />}
            dropDownIcon={
              <FontAwesomeIcon
                data-cy="profile-menu-dropdown"
                icon={FAR_ANGLE_DOWN}
              />
            }
            {...languageProps}
            logo={logoSrc}
            logoRouteLink={routes.HOME}
            profileName={givenName}
            profileIcon={<FontAwesomeIcon icon={FAS_USER_CIRCLE} />}
            {...(showBackButton && {
              backButtonIcon: <FontAwesomeIcon icon={FAL_ANGLE_LEFT} />,
              backButtonLabel: i18n({ button: 'accountManager' }) as string,
              handleBackButtonClick
            })}
            {...conditionalNavigationProps}
          />
          {isFeatureFlagEnabled('isSearchEnabled') && <Search />}
        </>
      )}
    </NavigationContainer>
  )
}

export default Header
